import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import {
  Archive, ArrowRightLeft, DoorOpen, ExternalLink, House, Laptop, LogOut, Monitor, Moon, Package, Plug, Settings, Sun, UserRound, Users, type LucideIcon,
} from 'lucide-react'
import { APP_NOMBRE, BACKEND_URL, LOGOS } from '../../config'
import { useAuth } from '../../contexts/AuthProvider'
import { useResumen } from '../../queries/useInventario'
import { PreferenciaTema, useTema } from '../../stores/temaStore'
import { iniciales } from '../../utils/login'
import { VEN_INVENTARIO } from '../../utils/roles'
import { RolApp } from '../../types/auth'
import { Menu } from '../kit'

interface Seccion {
  a: string
  texto: string
  Icono: LucideIcon
  soloStaff?: boolean
  /** Solo para cuentas con alguno de estos roles (docs/ROLES.md). */
  roles?: RolApp[]
  /** Cuántos quedan pendientes en la sección (dcc-sidebar__count); solo para quien los resuelve. */
  pendientes?: 'oficinas' | 'bajas' | 'traslados'
  /** No va en la barra inferior del celular (no caben más de cinco): se llega desde Inicio. */
  soloLateral?: boolean
}

/** Las secciones de la app. Agregar una página = agregar una línea aquí y su <Route>. */
const SECCIONES: Seccion[] = [
  { a: '/app', texto: 'Inicio', Icono: House },
  { a: '/app/oficinas', texto: 'Oficinas', Icono: DoorOpen, roles: VEN_INVENTARIO, pendientes: 'oficinas' },
  { a: '/app/bienes', texto: 'Bienes', Icono: Package, roles: VEN_INVENTARIO },
  { a: '/app/traslados', texto: 'Traslados', Icono: ArrowRightLeft, roles: VEN_INVENTARIO, pendientes: 'traslados' },
  { a: '/app/bajas', texto: 'Bajas', Icono: Archive, roles: ['administra_bienes'], pendientes: 'bajas', soloLateral: true },
  { a: '/app/teletrabajo', texto: 'Teletrabajo', Icono: Laptop, roles: ['administra_bienes'], soloLateral: true },
  { a: '/app/usuarios', texto: 'Usuarios', Icono: Users, soloStaff: true },
  { a: '/app/integraciones', texto: 'Integraciones', Icono: Plug, soloStaff: true },
]

const TEMAS: { clave: PreferenciaTema; texto: string; Icono: LucideIcon }[] = [
  { clave: 'claro', texto: 'Tema claro', Icono: Sun },
  { clave: 'oscuro', texto: 'Tema oscuro', Icono: Moon },
  { clave: 'sistema', texto: 'Como el sistema', Icono: Monitor },
]

/**
 * Marco de la app autenticada: la barra lateral del kit (`__brand` · `__action` ·
 * `__nav` · `__foot`) y el contenido. En móvil la navegación baja a la barra
 * flotante del kit (`dcc-bottom-nav--floating`) y el riel queda como banda superior
 * con la marca y el usuario; lo decide el CSS (styles/app.css), sin medir en JS.
 */
export default function AppLayout() {
  const { user, logout, puede } = useAuth()
  const { preferencia, elegir } = useTema()
  const navigate = useNavigate()
  const secciones = SECCIONES.filter((s) => (!s.soloStaff || user?.es_staff) && (!s.roles || puede(...s.roles)))
  const administra = puede('administra_bienes')
  const resumen = useResumen(puede(...VEN_INVENTARIO))
  // Oficinas cuya plancheta no está actualizada: lo que falta de la revisión.
  // Bajas: los bienes marcados para dar de baja (el proceso vendrá después).
  // Traslados: las solicitudes por resolver.
  // Cada contador, solo para quien lo resuelve.
  const pendientes = {
    oficinas: administra && resumen.data ? resumen.data.recintos - resumen.data.planchetas.ACTUALIZADA : 0,
    bajas: administra ? resumen.data?.bajas ?? 0 : 0,
    traslados: administra ? resumen.data?.solicitudes_pendientes ?? 0 : 0,
  }
  const QUE_FALTA = { oficinas: 'por actualizar', bajas: 'para dar de baja', traslados: 'por resolver' }
  const nombre = user?.persona?.nombre_corto || [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.username || ''

  const alElegir = async (clave: string) => {
    if (clave === 'perfil') navigate('/app/perfil')
    else if (clave === 'admin') window.location.href = `${BACKEND_URL}admin/`
    else if (clave === 'salir') {
      await logout()
      navigate('/login', { replace: true })
    } else elegir(clave as PreferenciaTema)
  }

  return (
    <div className="app">
      <aside className="dcc-sidebar app__lateral">
        <div className="dcc-sidebar__brand">
          <img className="dcc-logo-dcc-h" src={LOGOS.dccHorizontal} alt="DCC" width={405} height={75} />
          <hr className="dcc-sidebar__sep" />
          <p className="dcc-sidebar__app">{APP_NOMBRE}</p>
        </div>

        {/* NavLink pone aria-current="page" en la ruta activa: es lo que el kit marca. */}
        <nav className="dcc-sidebar__nav" aria-label="Secciones">
          {secciones.map(({ a, texto, Icono, pendientes: clave }) => (
            <NavLink key={a} to={a} end={a === '/app'} className="dcc-sidebar__item">
              <Icono className="dcc-i" aria-hidden />
              <span>{texto}</span>
              {clave && pendientes[clave] > 0 && (
                <span className="dcc-sidebar__count" aria-label={`${pendientes[clave]} ${QUE_FALTA[clave]}`}>{pendientes[clave]}</span>
              )}
            </NavLink>
          ))}
          {user?.es_staff && (
            <>
              <hr className="dcc-sidebar__sep" />
              <a className="dcc-sidebar__item" href={`${BACKEND_URL}admin/`}>
                <ExternalLink className="dcc-i" aria-hidden />
                <span>Administración</span>
              </a>
            </>
          )}
        </nav>

        <div className="dcc-sidebar__foot">
          <hr className="dcc-sidebar__sep" />
          {/* Abre hacia arriba: en el pie de la barra, hacia abajo se saldría de la pantalla. */}
          <Menu
            className="dcc-sidebar__user"
            claseDisparador="dcc-sidebar__gear"
            hacia="arriba"
            alineado="fin"
            etiqueta="Cuenta y preferencias"
            antes={
              <>
                <span className="dcc-sidebar__avatar" aria-hidden>{iniciales(nombre)}</span>
                <span className="dcc-sidebar__who">
                  <b>{nombre}</b>
                  <small>{user?.es_staff ? 'Administración' : user?.persona?.rut_formateado || 'Cuenta local'}</small>
                </span>
              </>
            }
            disparador={<Settings className="dcc-i" aria-hidden />}
            opciones={[
              { clave: 'perfil', texto: 'Mi perfil', Icono: UserRound, grupo: 0 },
              ...TEMAS.map((t) => ({ ...t, grupo: 1, marcada: preferencia === t.clave })),
              ...(user?.es_staff ? [{ clave: 'admin', texto: 'Administración', Icono: ExternalLink, grupo: 2 }] : []),
              { clave: 'salir', texto: 'Cerrar sesión', Icono: LogOut, grupo: 2 },
            ]}
            onElegir={alElegir}
          />
        </div>
      </aside>

      <main className="app__cuerpo" id="contenido">
        <div className="app__contenedor">
          <Outlet />
        </div>
      </main>

      <nav className="dcc-bottom-nav dcc-bottom-nav--floating app__barra-inferior" aria-label="Navegación principal">
        {/* Lo de administración no va en la barra inferior: se abre desde Inicio. */}
        {secciones.filter((s) => !s.soloStaff && !s.soloLateral).map(({ a, texto, Icono }) => (
          <NavLink key={a} to={a} end={a === '/app'} className="dcc-bottom-nav__item">
            <span className="dcc-bottom-nav__ico"><Icono className="dcc-i" aria-hidden /></span>
            {texto}
          </NavLink>
        ))}
      </nav>
    </div>
  )
}
